/** Passport PKCE credentials, isolated by application. Only access tokens reach JavaScript. */
export function createPassportAuth(client: 'storefront' | 'admin' | 'b2b', apiBase: string, options: {
    proxyOrigin?: string
    language?: () => string
    restoreOnlyAfterLogin?: boolean
} = {}) {
    let token: string | null = null
    let expiresAt = 0
    let profile: any = null
    let refreshing: Promise<string | null> | null = null
    let restoring: Promise<string | null> | null = null
    let csrf: Promise<string> | null = null
    let channel: BroadcastChannel | null = null
    const listeners = new Set<() => void>()
    const expiredListeners = new Set<() => void>()
    const logoutKey = `webixty_logout_${client}`
    const loginKey = `webixty_login_${client}`
    const tokenKey = `webixty_token_${client}`
    const browser = () => typeof window !== 'undefined'
    const root = () => new URL(apiBase.replace(/\/admin\/?$/, ''), window.location.origin).href.replace(/\/$/, '')
    const mapOrigin = (url: string, from: string, to: string) => {
        const value = new URL(url)
        if (value.origin === from) {
            const destination = new URL(to)
            value.protocol = destination.protocol
            value.hostname = destination.hostname
            value.port = destination.port
        }
        return value.href
    }
    const loggedOutAt = () => Number(localStorage.getItem(logoutKey) || 0)
    const clear = () => {
        token = null; expiresAt = 0; profile = null
        if (browser()) { localStorage.removeItem(tokenKey); sessionStorage.removeItem(tokenKey) }
        listeners.forEach(listener => listener())
    }
    const subscribe = () => {
        if (!channel && browser() && typeof BroadcastChannel !== 'undefined') {
            channel = new BroadcastChannel(`webixty_auth_${client}`)
            channel.onmessage = ({ data }) => {
                if (data?.type === 'logout') { clear(); restoring = null }
                if (data?.type === 'token' && data.issuedAt > loggedOutAt()) {
                    token = data.token; expiresAt = data.expiresAt; profile = data.user || null
                }
            }
        }
    }
    const failure = (status: number, data: any, retryAfter?: string | null) => Object.assign(new Error(
        status >= 500 ? 'Сървърът не успя да завърши входа. Опитайте отново.'
            : data?.message || 'Входът не можа да бъде завършен. Опитайте отново.'
    ), { status, response: { status, data: data || {} }, data,
        retryAfter: retryAfter && Number.isFinite(Number(retryAfter)) && Number(retryAfter) > 0 ? Math.ceil(Number(retryAfter)) : null })
    const xsrfCookie = () => typeof document === 'undefined' ? null
        : document.cookie.split('; ').find(cookie => cookie.startsWith('XSRF-TOKEN='))?.slice('XSRF-TOKEN='.length)
    async function json(path: string, body?: Record<string, unknown>, needsCsrf = true, retried = false, bearer?: string | null,
        authorization?: { callback: string, state: string }): Promise<any> {
        const headers: Record<string, string> = { Accept: 'application/json' }
        if (options.language) headers['Accept-Language'] = options.language()
        if (body) headers['Content-Type'] = 'application/json'
        if (bearer) headers.Authorization = `Bearer ${bearer}`
        if (body && needsCsrf) {
            const cookie = !retried && xsrfCookie()
            if (!csrf && cookie) headers['X-XSRF-TOKEN'] = decodeURIComponent(cookie)
            else {
                csrf ??= json('/auth/csrf', undefined, false).then(data => data.csrf_token).catch(error => { csrf = null; throw error })
                headers['X-CSRF-TOKEN'] = await csrf
            }
        }
        let response: Response
        try {
            const url = path.startsWith('https://') || path.startsWith('http://') ? path : root() + path
            response = await fetch(options.proxyOrigin ? mapOrigin(url, new URL(root()).origin, options.proxyOrigin) : url,
                { method: body ? 'POST' : 'GET', credentials: 'include', headers,
                ...(body ? { body: JSON.stringify(body) } : {}), cache: 'no-store', signal: AbortSignal.timeout(20000) })
        } catch (error: any) {
            if (error?.name === 'TimeoutError') throw new Error('Заявката отне твърде дълго. Опитайте отново.')
            throw error
        }
        if (authorization && response.ok) {
            const callback = new URL(options.proxyOrigin
                ? mapOrigin(response.url, options.proxyOrigin, new URL(root()).origin) : response.url)
            if (callback.origin + callback.pathname !== authorization.callback
                || callback.searchParams.get('state') !== authorization.state || callback.searchParams.has('error')) throw failure(401, null)
        }
        const data = await response.json().catch(() => null)
        if (!response.ok) {
            if (response.status === 419) {
                csrf = null
                if (body && !retried) return json(path, body, needsCsrf, true, bearer, authorization)
            }
            throw failure(response.status, data, response.headers?.get('Retry-After'))
        }
        if (!data || typeof data !== 'object') throw failure(502, null)
        return data
    }
    function receive(data: any, issuedAt: number) {
        if (issuedAt <= loggedOutAt()) return null
        if (!data?.access_token || !Number.isFinite(data.expires_in) || data.expires_in <= 0) throw failure(502, null)
        token = data.access_token
        expiresAt = Date.now() + Number(data.expires_in) * 1000
        profile = data.user || null
        localStorage.removeItem(tokenKey); sessionStorage.removeItem(tokenKey)
        channel?.postMessage({ type: 'token', token, expiresAt, issuedAt, user: profile })
        return token
    }
    async function refresh(staleToken?: string | null): Promise<string | null> {
        if (!browser()) return null
        subscribe()
        if (refreshing) return refreshing
        const perform = async () => {
            if (token && Date.now() < expiresAt - 10000 && (!staleToken || token !== staleToken)) return token
            const issuedAt = Date.now()
            const previousLogin = localStorage.getItem(loginKey)
            try {
                const response = await json('/auth/refresh', { client })
                if (response.data?.csrf_token) csrf = Promise.resolve(response.data.csrf_token)
                return receive(response.data, issuedAt)
            } catch (error: any) {
                if (error.response?.status === 401) {
                    const wasAuthenticated = !!token
                    clear()
                    if (options.restoreOnlyAfterLogin && localStorage.getItem(loginKey) === previousLogin) {
                        localStorage.removeItem(loginKey)
                    }
                    if (wasAuthenticated) expiredListeners.forEach(listener => listener())
                    return null
                }
                throw error
            }
        }
        const work = () => navigator.locks
            ? navigator.locks.request(`webixty_refresh_${client}`, perform)
            : perform()
        refreshing = work().finally(() => { refreshing = null })
        return refreshing
    }
    return {
        onLogout(listener: () => void) { listeners.add(listener); return () => listeners.delete(listener) },
        onSessionExpired(listener: () => void) { expiredListeners.add(listener); return () => expiredListeners.delete(listener) },
        async prepare() {
            if (!browser() || xsrfCookie()) return
            csrf ??= json('/auth/csrf', undefined, false).then(data => data.csrf_token).catch(error => { csrf = null; throw error })
            await csrf
        },
        async restore(onAuthorized?: (data: any) => void) {
            if (!browser()) return null
            subscribe()
            // Clear credentials left by the retired authentication implementation.
            localStorage.removeItem('token')
            localStorage.removeItem(tokenKey); sessionStorage.removeItem(tokenKey)
            // This timestamp is only a hint to try the HttpOnly session, never a credential.
            if (!token && options.restoreOnlyAfterLogin && !Number(localStorage.getItem(loginKey))) return null
            if (!token && loggedOutAt() && loggedOutAt() >= Number(localStorage.getItem(loginKey) || 0)) return null
            if (!token || Date.now() >= expiresAt - 10000) {
                restoring ??= refresh().finally(() => { restoring = null })
                await restoring
            }
            if (token && profile) onAuthorized?.({ user: profile })
            return token
        },
        async accessToken() {
            if (!browser()) return null
            return token && Date.now() >= expiresAt - 10000 ? refresh(token) : token
        },
        refresh,
        async login(path: string, credentials: Record<string, unknown>, returnTo = '/', onAuthorized?: (data: any) => void) {
            subscribe()
            const issuedAt = Date.now()
            const response = await json(path, credentials)
            const location = new URL(response.data?.authorization_url)
            const backend = new URL(root())
            const callback = root() + '/auth/callback/' + client
            const state = location.searchParams.get('state')
            if (location.origin !== backend.origin || !location.pathname.endsWith('/oauth/authorize')
                || location.searchParams.get('redirect_uri') !== callback || !state) throw failure(401, null)
            const receipt = await json(location.href, undefined, false, false, null, { callback, state })
            const data = { ...response.data, ...receipt.data }
            if (!data.user?.id) throw failure(502, null)
            if (!receive(data, issuedAt)) throw failure(401, null)
            localStorage.setItem(loginKey, String(issuedAt))
            csrf = data.csrf_token ? Promise.resolve(data.csrf_token) : null
            onAuthorized?.(data)
            return returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/'
        },
        async logout() {
            await json('/auth/browser-logout', { client }, true, false, token)
            localStorage.setItem(logoutKey, String(Date.now()))
            clear(); restoring = null; csrf = null
            channel?.postMessage({ type: 'logout' })
        },
        clear,
        invalidate() {
            localStorage.setItem(logoutKey, String(Date.now()))
            clear(); restoring = null
            channel?.postMessage({ type: 'logout' })
        },
    }
}
